import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Toggle } from '@delmonicos/shared-design-system-react/src/components/toggle/toggle';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useUpdateLocationFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.form-validation';
import { UpdateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.model';
import { RegularHoursModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/location/regular-hours.model';
import {
  Facility,
  LocationType,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import groupedFacilities from '@app/feature/location/facilities';

const LocationUpdateFormStep1InformationFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } =
    useFormContext<Pick<UpdateLocationModel, 'locationId' | 'location'>>();
  const { locationName, locationType } = useUpdateLocationFormValidation();

  return (
    <Card gap={GapEnum.L} direction="column" flex={1}>
      <AccentTitle
        icon="map-01"
        text={t('$.location-create.step-1.information.title')}
        variant={AccentVariantEnum.Primary}
      />
      <Stack direction="column" gap={GapEnum.M} flexWrap="wrap">
        <Controller
          name="location.name"
          control={control}
          rules={locationName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.name')}
              placeholder={t('delmonicos-shared-sdk.outputs-location.name')}
              required={!!locationName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="location.type"
          control={control}
          rules={locationType}
          render={({ field, fieldState: { error } }) => (
            <Stack gap={GapEnum.XS} direction="column">
              <Text>
                {t('delmonicos-shared-sdk.outputs-location.type')}{' '}
                <Text color={TextColor.Error}>*</Text>
              </Text>
              <Dropdown
                {...field}
                value={field.value ?? ''}
                onChange={(value) => field.onChange(value ?? '')}>
                <Dropdown.Option hidden disabled key="" value="" />
                {Object.values(LocationType).map((ty) => (
                  <Dropdown.Option key={ty} value={ty}>
                    {t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.location-type.${ty}`,
                    )}
                  </Dropdown.Option>
                ))}
              </Dropdown>
              {error?.message && (
                <Text color={TextColor.Error}>{error.message}</Text>
              )}
            </Stack>
          )}
        />
      </Stack>
    </Card>
  );
};

interface LocationUpdateFormStep1OpeningHoursWeekdayOptions {
  value: number;
  label: string;
}

const LocationUpdateFormStep1OpeningHoursFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control, setValue, watch } =
    useFormContext<Pick<UpdateLocationModel, 'locationId' | 'location'>>();
  const {
    locationOpeningTimesRegularHours,
    locationOpeningTimesTwentyfourseven,
  } = useUpdateLocationFormValidation();
  const regularHoursWatch = watch('location.openingTimes.regularHours');
  const twentyfoursevenWatch = watch('location.openingTimes.twentyfourseven');
  const [allDays, setAllDays] = useState<Omit<RegularHoursModel, 'weekday'>>();

  const weekdayOptions: LocationUpdateFormStep1OpeningHoursWeekdayOptions[] = [
    {
      value: 1,
      label: t('delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.MONDAY'),
    },
    {
      value: 2,
      label: t('delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.TUESDAY'),
    },
    {
      value: 3,
      label: t(
        'delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.WEDNESDAY',
      ),
    },
    {
      value: 4,
      label: t(
        'delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.THURSDAY',
      ),
    },
    {
      value: 5,
      label: t('delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.FRIDAY'),
    },
    {
      value: 6,
      label: t(
        'delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.SATURDAY',
      ),
    },
    {
      value: 7,
      label: t('delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.SUNDAY'),
    },
  ];

  const handleDayCheckChange = (
    day: LocationUpdateFormStep1OpeningHoursWeekdayOptions,
    isChecked: boolean,
  ) => {
    let newRegularHours = regularHoursWatch ?? [];

    if (isChecked) {
      newRegularHours.push({
        weekday: day.value,
        periodBegin: '00:00',
        periodEnd: '23:59',
      });
    } else {
      newRegularHours = newRegularHours.filter((d) => d.weekday !== day.value);
    }

    setValue('location.openingTimes.regularHours', newRegularHours);

    if (allDays) {
      setAllDays(undefined);
    }
  };

  const handlePeriodBeginChange = (
    day: LocationUpdateFormStep1OpeningHoursWeekdayOptions,
    time: string,
  ) => {
    const newRegularHours = (regularHoursWatch ?? []).map((hour) =>
      hour.weekday === day.value ? { ...hour, periodBegin: time } : hour,
    );

    setValue('location.openingTimes.regularHours', newRegularHours);
  };

  const handlePeriodEndChange = (
    day: LocationUpdateFormStep1OpeningHoursWeekdayOptions,
    time: string,
  ) => {
    const newRegularHours = (regularHoursWatch ?? []).map((hour) =>
      hour.weekday === day.value ? { ...hour, periodEnd: time } : hour,
    );

    setValue('location.openingTimes.regularHours', newRegularHours);
  };

  const handleAllDaysCheckChange = (isChecked: boolean) => {
    if (isChecked) {
      setAllDays({
        periodBegin: '00:00',
        periodEnd: '23:59',
      });

      setValue(
        'location.openingTimes.regularHours',
        weekdayOptions.map((w) => ({
          weekday: w.value,
          periodBegin: '00:00',
          periodEnd: '23:59',
        })),
      );
    } else {
      setAllDays(undefined);
    }
  };

  const handleAllDaysPeriodBeginChange = (time: string) => {
    setAllDays((old) => ({
      periodEnd: old?.periodEnd ?? '23:59',
      periodBegin: time,
    }));

    const updatedRegularHours = weekdayOptions.map((w) => ({
      weekday: w.value,
      periodBegin: time,
      periodEnd: allDays?.periodEnd ?? '23:59',
    }));

    setValue('location.openingTimes.regularHours', updatedRegularHours);
  };

  const handleAllDaysPeriodEndChange = (time: string) => {
    setAllDays((old) => ({
      periodBegin: old?.periodBegin ?? '00:00',
      periodEnd: time,
    }));

    const updatedRegularHours = weekdayOptions.map((w) => ({
      weekday: w.value,
      periodBegin: allDays?.periodBegin ?? '00:00',
      periodEnd: time,
    }));

    setValue('location.openingTimes.regularHours', updatedRegularHours);
  };

  return (
    <Card direction="column" flex={1} gap={GapEnum.L}>
      <AccentTitle
        icon="clock"
        text={t('delmonicos-shared-sdk.outputs-location.opening-times')}
        variant={AccentVariantEnum.Secondary}
      />

      <Controller
        name="location.openingTimes.twentyfourseven"
        control={control}
        rules={locationOpeningTimesTwentyfourseven}
        render={({ field }) => (
          <Toggle
            label={t('delmonicos-shared-sdk.outputs-location.twentyfourseven')}
            id="twentyfourseven"
            checked={field.value}
            onChange={(v) => field.onChange(v)}
          />
        )}
      />

      {!twentyfoursevenWatch && (
        <Stack gap={GapEnum.S} flex={1}>
          <Stack flex={1}>
            <Checkbox
              id="allDays"
              label={t('$.location-update.opening-hours.all-days')}
              checked={!!allDays}
              onChange={(event) =>
                handleAllDaysCheckChange(event.target.checked)
              }
            />
          </Stack>
          <Stack flex={1} gap={GapEnum.S}>
            <TextInput
              label=""
              disabled={!allDays}
              type="time"
              value={allDays?.periodBegin ?? '00:00'}
              onChange={(e) => handleAllDaysPeriodBeginChange(e.target.value)}
            />
            <TextInput
              label=""
              disabled={!allDays}
              type="time"
              value={allDays?.periodEnd ?? '00:00'}
              onChange={(e) => handleAllDaysPeriodEndChange(e.target.value)}
            />
          </Stack>
        </Stack>
      )}

      {!twentyfoursevenWatch &&
        weekdayOptions.map((weekday) => {
          const regularHour = regularHoursWatch?.find(
            (r) => r.weekday === weekday.value,
          );

          return (
            <Stack gap={GapEnum.S} key={weekday.value} flex={1}>
              <Stack flex={1}>
                <Checkbox
                  id={`${weekday.value}`}
                  label={weekday.label}
                  checked={!!regularHour}
                  onChange={(event) =>
                    handleDayCheckChange(weekday, event.target.checked)
                  }
                />
              </Stack>
              <Stack flex={1} gap={GapEnum.S}>
                <TextInput
                  label=""
                  disabled={!regularHour || !!allDays}
                  type="time"
                  value={regularHour?.periodBegin ?? '00:00'}
                  onChange={(e) =>
                    handlePeriodBeginChange(weekday, e.target.value)
                  }
                />
                <TextInput
                  label=""
                  disabled={!regularHour || !!allDays}
                  type="time"
                  value={regularHour?.periodEnd ?? '00:00'}
                  onChange={(e) =>
                    handlePeriodEndChange(weekday, e.target.value)
                  }
                />
              </Stack>
            </Stack>
          );
        })}

      <Controller
        render={() => <div />}
        name="location.openingTimes.regularHours"
        control={control}
        rules={locationOpeningTimesRegularHours}
      />
    </Card>
  );
};

const LocationUpdateFormStep1FacilitiesFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control, watch, setValue } =
    useFormContext<Pick<UpdateLocationModel, 'locationId' | 'location'>>();

  const facilities = watch('location.facilities') ?? [];

  const toggleFacility = (facility: Facility) => {
    setValue(
      'location.facilities',
      facilities.includes(facility)
        ? facilities.filter((f) => f !== facility)
        : [...facilities, facility],
      { shouldDirty: true },
    );
  };

  const renderCheckbox = (facility: Facility) => (
    <Controller
      key={facility}
      name="location.facilities"
      control={control}
      render={() => (
        <Checkbox
          id={facility}
          checked={facilities.includes(facility)}
          onChange={() => toggleFacility(facility)}
          label={t(
            `delmonicos-shared-sdk.ocpi-protocol-enums.facility.${facility}`,
          )}
        />
      )}
    />
  );

  return (
    <Card flex={1} gap={GapEnum.L}>
      <AccentTitle
        icon="location"
        text={t('delmonicos-shared-sdk.outputs-location.facilities')}
        variant={AccentVariantEnum.Accent1}
      />

      <Stack direction="column" gap={GapEnum.L}>
        {groupedFacilities.map(({ title, facilities: options }) => (
          <Stack key={title} direction="column" gap={GapEnum.XS}>
            <Text>{t(title)}</Text>
            <Stack gap={GapEnum.XS} direction="column">
              {options.map(({ name }) => renderCheckbox(name))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
};

export const LocationUpdateFormStep1Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L} flex={1}>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <LocationUpdateFormStep1InformationFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <LocationUpdateFormStep1OpeningHoursFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <LocationUpdateFormStep1FacilitiesFragment />
      </Stack>
    </Stack>
  );
};
